<!-- Pre-Alert Input Modal - Fixed -->
<div class="modal fade pre-alert-modal"
     tabindex="-1"
     [class.show]="isVisible"
     [style.display]="isVisible ? 'block' : 'none'"
     (mousedown)="onBackdropMouseDown($event)"
     (keydown.escape)="closeModal()"
     *ngIf="isVisible"
     role="dialog"
     aria-labelledby="preAlertModalTitle"
     aria-hidden="false">
  <div class="modal-dialog modal-lg" (mousedown)="$event.stopPropagation()" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <!-- Modal Header -->
      <div class="modal-header">
        <h5 class="modal-title d-flex align-items-center" id="preAlertModalTitle">
          <i class="bi bi-truck me-2 text-white"></i>
          {{ modalTitle }}
        </h5>
        <button type="button" 
                class="btn-close" 
                (click)="closeModal()" 
                [disabled]="isLoading"
                aria-label="Close modal"></button>
      </div>

      <!-- Modal Body -->
      <div class="modal-body">
        <!-- Error Alert -->
        <div class="alert alert-danger d-flex align-items-center" *ngIf="error">
          <i class="fas fa-exclamation-triangle me-2"></i>
          {{ error }}
        </div>

        <!-- Loading State -->
        <div class="text-center py-4" *ngIf="isLoading">
          <div class="spinner-border text-primary me-2" role="status">
            <span class="visually-hidden">Processing...</span>
          </div>
          <span>Processing pre-alert information...</span>
        </div>

        <!-- Form Content -->
        <form [formGroup]="preAlertForm" *ngIf="!isLoading" novalidate>
          <div class="row">
            <!-- Bill of Lading -->
            <div class="col-md-6 mb-3">
              <label class="form-label d-flex align-items-center">
                Bill of Lading <span class="text-danger">*</span>
                <i class="fas fa-file-alt ms-2 text-success" 
                   *ngIf="isLadingBillAutoPopulated"
                   title="Auto-populated from filename"></i>
                <i class="fas fa-info-circle ms-1 text-muted" 
                   *ngIf="!isLadingBillAutoPopulated"
                   title="Unique identifier for the shipment"></i>
              </label>
              
              <!-- Input Group for Enhanced Lading Bill Field -->
              <div class="input-group">
                <input 
                  type="text" 
                  class="form-control"
                  [class.is-invalid]="hasFieldError('ladingBill')"
                  [class.bg-light]="isLadingBillAutoPopulated"
                  formControlName="ladingBill"
                  [placeholder]="isLadingBillAutoPopulated ? 'Auto-filled from filename' : 'e.g., BL-2025-001234'"
                  maxlength="50">
                  
                <span class="input-group-text" 
                      *ngIf="isLadingBillAutoPopulated"
                      [title]="'Auto-populated from: ' + autoPopulatedFilename">
                  <i class="fas fa-magic text-success"></i>
                </span>
              </div>
              
              <!-- Auto-populated indicator text -->
              <div class="form-text text-success" *ngIf="isLadingBillAutoPopulated">
                <i class="fas fa-file-alt me-1"></i>
                Auto-populated from filename: <strong>{{ autoPopulatedFilename }}</strong>
              </div>
              
              <!-- Manual entry help text with restore option -->
              <div class="form-text" *ngIf="!isLadingBillAutoPopulated">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="text-muted">Enter a unique identifier for this shipment</span>
                  <button 
                    type="button" 
                    class="btn btn-link btn-sm p-0 text-decoration-none"
                    *ngIf="filename"
                    (click)="restoreFilenameAutoPopulation()"
                    title="Restore filename as Bill of Lading">
                    <i class="fas fa-undo me-1"></i>
                    Use filename
                  </button>
                </div>
              </div>
              
              <div class="invalid-feedback" *ngIf="hasFieldError('ladingBill')">
                {{ getFieldError('ladingBill') }}
              </div>
            </div>

            <!-- Transport Company -->
            <div class="col-md-6 mb-3">
              <label class="form-label">
                Transport Company
                <i class="fas fa-info-circle ms-1 text-muted" 
                   title="Name of the shipping/transport company"></i>
              </label>
              <input 
                type="text" 
                class="form-control"
                [class.is-invalid]="hasFieldError('transportCompany')"
                formControlName="transportCompany"
                placeholder="e.g., Maersk Line"
                maxlength="100">
              <div class="invalid-feedback" *ngIf="hasFieldError('transportCompany')">
                {{ getFieldError('transportCompany') }}
              </div>
            </div>
          </div>

          <div class="row">
            <!-- Departure Location -->
            <div class="col-md-6 mb-3">
              <label class="form-label">
                Departure Location <span class="text-danger">*</span>
                <i class="fas fa-info-circle ms-1 text-muted" 
                   title="Origin port or location"></i>
              </label>
              <input 
                type="text" 
                class="form-control"
                [class.is-invalid]="hasFieldError('departureLocation')"
                formControlName="departureLocation"
                placeholder="e.g., Shanghai Port, China"
                maxlength="100">
              <div class="invalid-feedback" *ngIf="hasFieldError('departureLocation')">
                {{ getFieldError('departureLocation') }}
              </div>
            </div>

            <!-- Arrival Location -->
            <div class="col-md-6 mb-3">
              <label class="form-label">
                Arrival Location <span class="text-danger">*</span>
                <i class="fas fa-info-circle ms-1 text-muted" 
                   title="Destination port or location"></i>
              </label>
              <input 
                type="text" 
                class="form-control"
                [class.is-invalid]="hasFieldError('arrivalLocation')"
                formControlName="arrivalLocation"
                placeholder="e.g., Manila Port, Philippines"
                maxlength="100">
              <div class="invalid-feedback" *ngIf="hasFieldError('arrivalLocation')">
                {{ getFieldError('arrivalLocation') }}
              </div>
            </div>
          </div>

          <div class="row">
            <!-- ETD (Estimated Time of Departure) -->
            <div class="col-md-6 mb-3">
              <label class="form-label">
                Estimated Time of Departure <span class="text-danger">*</span>
                <i class="fas fa-info-circle ms-1 text-muted" 
                   title="When the shipment is expected to depart"></i>
              </label>
              <input 
                type="datetime-local" 
                class="form-control"
                [class.is-invalid]="hasFieldError('etd')"
                formControlName="etd">
              <div class="invalid-feedback" *ngIf="hasFieldError('etd')">
                {{ getFieldError('etd') }}
              </div>
            </div>

            <!-- ETA (Estimated Time of Arrival) -->
            <div class="col-md-6 mb-3">
              <label class="form-label">
                Estimated Time of Arrival <span class="text-danger">*</span>
                <i class="fas fa-info-circle ms-1 text-muted" 
                   title="When the shipment is expected to arrive"></i>
              </label>
              <input 
                type="datetime-local" 
                class="form-control"
                [class.is-invalid]="hasFieldError('eta')"
                formControlName="eta">
              <div class="invalid-feedback" *ngIf="hasFieldError('eta')">
                {{ getFieldError('eta') }}
              </div>
            </div>
          </div>

          <!-- Weight Section with Auto-Fill -->
          <div class="card mb-3">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h6 class="mb-0">
                <i class="fas fa-weight-hanging me-2"></i>
                Weight Information
              </h6>
              <div class="form-check form-switch">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="autoFillWeights" 
                  [checked]="autoFillWeights"
                  (change)="toggleAutoFillWeights()">
                <label class="form-check-label small" for="autoFillWeights">
                  Auto-fill weights
                </label>
              </div>
            </div>
            <div class="card-body">
              <div class="row">
                <!-- Actual Weight -->
                <div class="col-md-4 mb-3">
                  <label class="form-label">
                    Actual Weight (kg) <span class="text-danger">*</span>
                    <i class="fas fa-info-circle ms-1 text-muted" 
                       title="Physical weight of the shipment"></i>
                  </label>
                  <input 
                    type="number" 
                    class="form-control"
                    [class.is-invalid]="hasFieldError('weight')"
                    formControlName="weight"
                    placeholder="0.0"
                    min="0.1"
                    max="50000"
                    step="0.1">
                  <div class="invalid-feedback" *ngIf="hasFieldError('weight')">
                    {{ getFieldError('weight') }}
                  </div>
                </div>

                <!-- Chargeable Weight -->
                <div class="col-md-4 mb-3">
                  <label class="form-label">
                    Chargeable Weight (kg)
                    <i class="fas fa-info-circle ms-1 text-muted" 
                       title="Weight used for billing calculations"></i>
                  </label>
                  <input 
                    type="number" 
                    class="form-control"
                    [class.is-invalid]="hasFieldError('chargeableWeight')"
                    formControlName="chargeableWeight"
                    placeholder="Auto-filled"
                    min="0.1"
                    max="50000"
                    step="0.1">
                  <div class="invalid-feedback" *ngIf="hasFieldError('chargeableWeight')">
                    {{ getFieldError('chargeableWeight') }}
                  </div>
                </div>

                <!-- Volumetric Weight -->
                <div class="col-md-4 mb-3">
                  <label class="form-label">
                    Volumetric Weight (kg)
                    <i class="fas fa-info-circle ms-1 text-muted" 
                       title="Weight based on package dimensions"></i>
                  </label>
                  <input 
                    type="number" 
                    class="form-control"
                    [class.is-invalid]="hasFieldError('volumetricWeight')"
                    formControlName="volumetricWeight"
                    placeholder="Auto-filled"
                    min="0.1"
                    max="50000"
                    step="0.1">
                  <div class="invalid-feedback" *ngIf="hasFieldError('volumetricWeight')">
                    {{ getFieldError('volumetricWeight') }}
                  </div>
                </div>
              </div>

              <!-- Weight Reset Button -->
              <div class="text-end" *ngIf="autoFillWeights">
                <button 
                  type="button" 
                  class="btn btn-outline-secondary btn-sm"
                  (click)="resetWeightFields()"
                  title="Clear auto-filled weight values">
                  <i class="fas fa-undo me-1"></i>
                  Reset Weight Fields
                </button>
              </div>
            </div>
          </div>

          <div class="row">
            <!-- Number of Pieces -->
            <div class="col-md-6 mb-3">
              <label class="form-label d-flex align-items-center">
                Number of Pieces
                <i class="fas fa-calculator ms-2 text-primary" 
                   *ngIf="isPiecesComputed"
                   title="Auto-calculated from unique carton numbers"></i>
                <i class="fas fa-info-circle ms-1 text-muted" 
                   *ngIf="!isPiecesComputed"
                   title="Total number of packages/cartons"></i>
              </label>
              
              <!-- Input Group for Enhanced Pieces Field -->
              <div class="input-group">
                <input 
                  type="number" 
                  class="form-control"
                  [class.is-invalid]="hasFieldError('pieces')"
                  [class.bg-light]="isPiecesComputed"
                  formControlName="pieces"
                  [placeholder]="isPiecesComputed ? 'Auto-calculated' : 'e.g., 25'"
                  [readonly]="isPiecesComputed"
                  min="1"
                  max="10000"
                  step="1">
                  
                <span class="input-group-text" 
                      *ngIf="isPiecesComputed"
                      [title]="computedPiecesText">
                  <i class="fas fa-check-circle text-success"></i>
                </span>
              </div>
              
              <!-- Auto-calculated indicator text -->
              <div class="form-text text-success" *ngIf="isPiecesComputed">
                <i class="fas fa-magic me-1"></i>
                {{ computedPiecesText }} (auto-calculated)
              </div>
              
              <!-- Manual entry help text -->
              <div class="form-text text-muted" *ngIf="!isPiecesComputed">
                Enter the total number of packages or cartons for this shipment
              </div>
              
              <div class="invalid-feedback" *ngIf="hasFieldError('pieces')">
                {{ getFieldError('pieces') }}
              </div>
            </div>

            <!-- Vessel Number -->
            <div class="col-md-6 mb-3">
              <label class="form-label">
                Vessel/Flight Number
                <i class="fas fa-info-circle ms-1 text-muted" 
                   title="Vessel or flight identifier"></i>
              </label>
              <input 
                type="text" 
                class="form-control"
                [class.is-invalid]="hasFieldError('vesselNumber')"
                formControlName="vesselNumber"
                placeholder="e.g., MSC OSCAR or MH370"
                maxlength="50">
              <div class="invalid-feedback" *ngIf="hasFieldError('vesselNumber')">
                {{ getFieldError('vesselNumber') }}
              </div>
            </div>
          </div>

          <!-- Form Summary (for debugging in development) -->
          <div class="alert alert-info small" *ngIf="hasFormErrors">
            <i class="fas fa-exclamation-circle me-2"></i>
            Please complete all required fields and correct any validation errors above.
          </div>
        </form>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <div class="d-flex justify-content-between w-100">
          <!-- Form Status -->
          <div class="d-flex align-items-center">
            <span class="badge bg-success me-2" *ngIf="isFormValid">
              <i class="fas fa-check me-1"></i>
              Form Valid
            </span>
            <span class="badge bg-warning" *ngIf="!isFormValid && !isLoading">
              <i class="fas fa-exclamation-triangle me-1"></i>
              {{ getErrorCount() }} Error(s)
            </span>
          </div>

          <!-- Action Buttons -->
          <div>
            <button 
              type="button" 
              class="btn btn-secondary me-2" 
              (click)="onCancel()" 
              [disabled]="isLoading">
              <i class="fas fa-times me-1"></i>
              Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              (click)="onSave()" 
              [disabled]="!isFormValid || isLoading">
              <div class="spinner-border spinner-border-sm me-2" *ngIf="isLoading" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <i class="fas fa-save me-1" *ngIf="!isLoading"></i>
              {{ saveButtonText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Modal Backdrop - Fixed -->
<div class="modal-backdrop fade pre-alert-backdrop" 
     [class.show]="isVisible" 
     *ngIf="isVisible"></div>